import { Validators } from '@angular/forms';
import {
    DEFAULT_FLOW_LOSS_PERCENTAGE,
    DEPTH,
    FLOW,
    HIGH,
    LOW,
    MEDIUM,
    QCONTINUITY,
    QCONTINUITY_ENTITY,
    QFLUME,
    QFLUME_ENTITY,
    QLOOKUP,
    QLOOKUP_ENTITY,
    QWEIR,
    QWEIR_ENTITY,
} from 'app/shared/constant';
import { LocationStatus } from 'app/shared/models/locations';
import { MonitorAddLocationSelectable, MonitorIdentifier } from 'app/shared/models/monitorsetting';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import {
    DepthDeviceName,
    DepthDeviceType,
    VelocityDeviceName,
    VelocityDeviceType,
} from 'app/shared/enums/device-type-code';
import {
    SensorElement,
    MonitoringPointElement,
    SampleRateElement,
    MaxVelocityElement,
    SensorPreference,
    ChannelElement,
    ComportOption,
    SamplerTypeOption,
    FlowTypeOption,
    FlowType,
    ScanRateElement,
} from 'app/shared/components/location-card/models/location-card.model';
import { Selectable } from 'app/shared/models/selectable';
import { AvailableDynamicSection } from './store/models/dynamic-section-models';

export const BATTERY_LOW_THRESHOLD_DEFAULT_VALUE = 7.5;

export const InstallationTypeValidators = function (isMetric: boolean) {
    return [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(
            isMetric ? REGEX_CONFIG.installationMetricValuePattern : REGEX_CONFIG.installationValuePattern,
        ),
    ];
};
export const NoneSensor: SensorElement = { id: 0, value: '', text: 'None' };
export const NoneDynamicSection: AvailableDynamicSection = { name: 'None', sensorType: 0, version: '0' };


enum FlowLossType {
    Flow = 0,
    Depth = 1,
}

enum FlowLossSensitivity {
    Low = 0,
    Medium = 1,
    High = 2,
}

export enum LocationCommunicationType {
    Default = 0,
    Landline = 1,
    DMI = 2,
    Serial = 3,
    Wireless = 4,
    FTP = 5,
    Bluetooth = 6,
    External = 7,
}

const MP_ONE: MonitoringPointElement = { id: 1, value: 0, text: 'MP1' };
const MP_TWO: MonitoringPointElement = { id: 2, value: 1, text: 'MP2' };

const CHANNEL_ONE = { id: 1, value: 1, text: 'CH1' };
const CHANNEL_TWO = { id: 2, value: 2, text: 'CH2' };

export const VELOCITY_AUTO = { id: 1, value: 0, text: 'Auto' };
export const VELOCITY_MANUAL = { id: 2, value: 1, text: 'Manual' };
export const CUSTOM_TYPE_VALUE = 'Pipe: 211';


export enum StorageOptions {
    StoreEveryReading = 0,
    StoreMidnight,
}

export enum MonitorSeriesNames {
    triton = 'TRITON+',
    echo = 'ECHO',
    rainAlert = 'RAINALERT III',
    ForeSITE_UL = 'FORESITE-UL',
    ForeSITE_XD = 'FORESITE-XD',
    usgs = 'USGS',
    flex = 'FLEX',
    genericMonitor = 'Generic Monitor',
    edgeDx = 'EDGE-DX',
    edgeRl = 'EDGE-RL',
}

export enum BaseMonitorSeriesValues {
    TRITON_PLUS = 'TRITON+',
    ECHO = 'ECHO',
    RAINALERT_III = 'RAINALERT III',
    FORESITE_UL = 'ForeSITE-UL',
    FORESITE_XD = 'FORESITE-XD',
    FLEX = 'FLEX',
    GENERIC_MONITOR = 'GENERIC MONITOR',
    EDGE_DX = 'EDGE-DX',
    EDGE_RL = 'EDGE-RL',
}

export enum SensorNames {
    PARADEPTH = 'PARADEPTH'
}

export const BASE_MONITOR_TO_ENUM_MAP: Record<BaseMonitorSeriesValues, MonitorSeriesNames> = {
    [BaseMonitorSeriesValues.TRITON_PLUS]: MonitorSeriesNames.triton,
    [BaseMonitorSeriesValues.ECHO]: MonitorSeriesNames.echo,
    [BaseMonitorSeriesValues.RAINALERT_III]: MonitorSeriesNames.rainAlert,
    [BaseMonitorSeriesValues.FORESITE_UL]: MonitorSeriesNames.ForeSITE_UL,
    [BaseMonitorSeriesValues.FORESITE_XD]: MonitorSeriesNames.ForeSITE_XD,
    [BaseMonitorSeriesValues.FLEX]: MonitorSeriesNames.flex,
    [BaseMonitorSeriesValues.GENERIC_MONITOR]: MonitorSeriesNames.genericMonitor,
    [BaseMonitorSeriesValues.EDGE_DX]: MonitorSeriesNames.edgeDx,
    [BaseMonitorSeriesValues.EDGE_RL]: MonitorSeriesNames.edgeRl,
};

export enum LocType {
    location = 'location',
    locationGroup = 'locationGroup',
}

export enum TritonSensorNames {
    peakCombo = 'Peak Combo Sensor',
    avMax = 'AV|Max / Peak Combo Sensor',
    avGated = 'AV|Gated Sensor',
    smartDepth = 'Smart Depth Sensor',
    paraDepth = 'ParaDepth / Smart Depth Sensor',
    surfaceCombo = 'Surface Combo Sensor',
    paraFlow = 'ParaFlow / Surface Combo Sensor',
    longRange = 'Long Range Depth sensor',
}

export enum ForeSITESensors {
    radar = 'Radar',
}

export const MonitorSeries = <Array<MonitorAddLocationSelectable>>[
    {
        monitorSeriesID: MonitorIdentifier.TritonPlus,
        value: 'TRITON+',
        text: 'TRITON+',
    },
    {
        monitorSeriesID: MonitorIdentifier.Echo,
        value: 'ECHO',
        text: 'ECHO',
    },
    {
        monitorSeriesID: MonitorIdentifier.RainAlertIII,
        value: 'RAINALERT III',
        text: 'RainAlert III',
    },
    {
        monitorSeriesID: MonitorIdentifier.ForeSITE_UL,
        value: 'FORESITE-UL',
        text: 'ForeSITE-UL',
    },
    {
        monitorSeriesID: MonitorIdentifier.ForeSITE_XD,
        value: 'FORESITE-XD',
        text: 'ForeSITE-XD',
    },
    {
        monitorSeriesID: MonitorIdentifier.USGS,
        value: 'USGS',
        text: 'USGS',
    },
];

export const FlowList = <Array<SampleRateElement>>[
    { id: 1, value: 0, text: 'Typical' },
    { id: 3, value: 2, text: 'Usually Dry' },
    { id: 2, value: 1, text: 'Wet/Stagnant' },
];

export const TritonSensorTemp = [
    { id: 1, value: 0, text: 'Compensated' },
    { id: 2, value: 1, text: 'Sensor' },
];

export const SensitivityList = <Array<SampleRateElement>>[
    { id: 1, value: 4, text: '4' },
    { id: 2, value: 5, text: '5' },
    { id: 3, value: 6, text: '6' },
    { id: 4, value: 7, text: '7' },
    { id: 5, value: 8, text: '8' },
    { id: 6, value: 9, text: '9' },
    { id: 7, value: 10, text: '10' },
    { id: 8, value: 11, text: '11' },
    { id: 9, value: 12, text: '12' },
    { id: 10, value: 13, text: '13' },
    { id: 11, value: 14, text: '14' },
    { id: 12, value: 15, text: '15' },

    { id: 13, value: 17, text: '17' },
    { id: 14, value: 20, text: '20' },
    { id: 15, value: 25, text: '25' },
    { id: 16, value: 50, text: '50' },
    { id: 17, value: 100, text: '100' },
    { id: 18, value: 200, text: '200' },
];

export const DeadBandList = (isMetric: boolean) => {
    return isMetric
        ? [
              { id: 2, value: 1, text: '25.4' },
              { id: 3, value: 2, text: '50.8' },
              { id: 4, value: 3, text: '76.2' },
              { id: 5, value: 4, text: '101.6' },
              { id: 6, value: 5, text: '127' },
              { id: 1, value: 0, text: 'Auto' },
          ]
        : [
              { id: 2, value: 1, text: '1' },
              { id: 3, value: 2, text: '2' },
              { id: 4, value: 3, text: '3' },
              { id: 5, value: 4, text: '4' },
              { id: 6, value: 5, text: '5' },
              { id: 1, value: 0, text: 'Auto' },
          ];
};

export const EchoSensorPower = [
    { id: 1, value: 0, text: 'Auto' },
    { id: 2, value: 1, text: '1' },
    { id: 3, value: 2, text: '2' },
    { id: 4, value: 3, text: '3' },
    { id: 5, value: 4, text: '4' },
    { id: 6, value: 5, text: '5' },
    { id: 7, value: 6, text: '6' },
    { id: 8, value: 7, text: '7' },
    { id: 9, value: 8, text: '8' },
    { id: 10, value: 9, text: '9' },
    { id: 11, value: 10, text: '10' },
];

export const TritonSensorPower = (isTriton: boolean) => {
    const powerList = [
        { id: 1, value: 0, text: 'Auto' },
        { id: 2, value: 1, text: '1' },
        { id: 3, value: 2, text: '2' },
        { id: 4, value: 3, text: '3' },
        { id: 5, value: 4, text: '4' },
        { id: 6, value: 5, text: '5' },
        { id: 7, value: 6, text: '6' },
        { id: 8, value: 7, text: '7' },
        { id: 9, value: 8, text: '8' },
        { id: 10, value: 9, text: '9' },
        { id: 11, value: 10, text: '10' },
    ];

    return isTriton ? [...powerList, { id: 12, value: 11, text: '11' }] : powerList;
};

export enum GainControlValue {
    Auto = 'Auto',
    Manual = 'Manual',
}

export const GAIN_CONTROL_AUTO_NUMERIC = 840;

export const GainControlOptions = [
    { id: 1, value: GainControlValue.Auto, text: GainControlValue.Auto },
    { id: 2, value: GainControlValue.Manual, text: GainControlValue.Manual },
];

export const GainValues = () => {
    return Array.from({ length: 49 }, (_, i) => ({ id: i + 1, value: i - 24, text: String(i - 24) }));
};

export const DIRECTION_LIST_DEFAULT_VALUE = 1;
export const MIN_MEASUREMENT_RANGE = 6000;

export const DirectionListTwoDirectional = [
    { id: 1, text: 'Forward', value: 1 },
    { id: 2, text: 'Backward', value: 2 },
];

export const DirectionListNonTwoDirectional = [
    { id: 2, value: 1, text: 'Forward' },
    { id: 4, value: 3, text: 'Backward Bidirectional' },
    { id: 3, value: 2, text: 'Backward' },
    { id: 1, value: 0, text: 'Bidirectional' },
];

export const DirectionList = (twoDirectional: boolean) => {
    if (twoDirectional) {
        return [
            { id: 1, text: 'Forward', value: 1 },
            { id: 2, text: 'Backward', value: 2 },
        ];
    }

    return <Array<SampleRateElement>>[
        { id: 2, value: 1, text: 'Forward' },
        { id: 4, value: 3, text: 'Backward Bidirectional' },
        { id: 3, value: 2, text: 'Backward' },
        { id: 1, value: 0, text: 'Bidirectional' },
    ];
};

export const MinVelocityMetricList = <Array<MaxVelocityElement>>[
    { id: 1, value: 0.01, text: '0.01' },
    { id: 2, value: 0.02, text: '0.02' },
    { id: 3, value: 0.05, text: '0.05' },
];

export const MinVelocityNonMetricList = <Array<MaxVelocityElement>>[
    { id: 1, value: 0.02, text: '0.02' },
    { id: 2, value: 0.05, text: '0.05' },
    { id: 3, value: 0.1, text: '0.10' },
    { id: 4, value: 0.2, text: '0.20' },
];

export const VelocityList = <Array<SampleRateElement>>[VELOCITY_AUTO, VELOCITY_MANUAL];

export const PrimarySensors = <Array<SensorElement>>[
    { id: 1, value: 'AV|Max / Peak Combo 1', text: 'AV|Max / Peak Combo Sensor', altValue: 'Peak Combo 1' },
    { id: 2, value: 'AV|Gated 1', text: 'AV|Gated Sensor', altValue: 'AV|Gated 1' },
    { id: 3, value: 'ParaDepth / Smart Depth 1', text: 'ParaDepth / Smart Depth Sensor', altValue: 'Smart Depth 1' },
    {
        id: 4,
        value: 'ParaFlow / Surface Combo 1',
        text: 'ParaFlow / Surface Combo Sensor',
        altValue: 'Surface Combo 1',
    },
    { id: 5, value: 'Long Range Depth 1', text: 'Long Range Depth sensor', altValue: 'Long Range Depth 1' },
];

export const ForeSITERadarSensor = { id: 1, value: 'Radar', text: 'Radar', altValue: 'Radar' };
export const ForeSITEXDSensors = <Array<SensorElement>>[ForeSITERadarSensor];
export const ForeSITEXDSensorOptions = [NoneSensor, ForeSITERadarSensor];
export const SecondarySensors = <Array<SensorElement>>[
    { id: 1, value: 'AV|Max / Peak Combo 2', text: 'AV|Max / Peak Combo Sensor', altValue: 'Peak Combo 2' },
    { id: 2, value: 'AV|Gated 2', text: 'AV|Gated Sensor', altValue: 'AV|Gated 2' },
    { id: 3, value: 'ParaDepth / Smart Depth 2', text: 'ParaDepth / Smart Depth Sensor', altValue: 'Smart Depth 2' },
    {
        id: 4,
        value: 'ParaFlow / Surface Combo 2',
        text: 'ParaFlow / Surface Combo Sensor',
        altValue: 'Surface Combo 2',
    },
    { id: 5, value: 'Long Range Depth 2', text: 'Long Range Depth sensor', altValue: 'Long Range Depth 2' },
];

export const DepthParaDepthUltrasonicName = 'Smart Depth';

export const DepthPreferences: Map<string, SensorPreference> = new Map([
    [DepthDeviceName.Auto, { id: 1, value: DepthDeviceType.Auto, text: 'Auto' }],
    [DepthDeviceName.UpwardCh1, { id: 2, value: DepthDeviceType.UpwardCh1, text: 'Upward Ultrasonic' }],
    [DepthDeviceName.UpwardCh2, { id: 3, value: DepthDeviceType.UpwardCh2, text: 'Upward Ultrasonic' }],
    [DepthDeviceName.PressureCh1, { id: 4, value: DepthDeviceType.PressureCh1, text: 'Pressure' }],
    [DepthDeviceName.PressureCh2, { id: 5, value: DepthDeviceType.PressureCh2, text: 'Pressure' }],
    [DepthDeviceName.UltrasonicCh1, { id: 6, value: DepthDeviceType.UltrasonicCh1, text: 'Surface Depth' }],
    [DepthDeviceName.UltrasonicCh2, { id: 7, value: DepthDeviceType.UltrasonicCh2, text: 'Surface Depth' }],
    [DepthDeviceName.LongRangeCh1, { id: 8, value: DepthDeviceType.LongRangeCh1, text: 'Long Range' }],
    [DepthDeviceName.LongRangeCh2, { id: 9, value: DepthDeviceType.LongRangeCh2, text: 'Long Range' }],
]);

export const VelocityPreferences: Map<string, SensorPreference> = new Map([
    [VelocityDeviceName.Auto, { id: 1, value: VelocityDeviceType.Auto, text: 'Auto' }],
    [VelocityDeviceName.AvGatedCh1, { id: 2, value: VelocityDeviceType.AvGatedCh1, text: 'AV|Gated Velocity' }],
    [VelocityDeviceName.AvGatedCh2, { id: 3, value: VelocityDeviceType.AvGatedCh2, text: 'AV|Gated Velocity' }],
    [VelocityDeviceName.PeakCh1, { id: 4, value: VelocityDeviceType.PeakCh1, text: 'Peak Velocity' }],
    [VelocityDeviceName.PeakCh2, { id: 5, value: VelocityDeviceType.PeakCh2, text: 'Peak Velocity' }],
    [VelocityDeviceName.SurfaceCh1, { id: 6, value: VelocityDeviceType.SurfaceCh1, text: 'Surface Velocity' }],
    [VelocityDeviceName.SurfaceCh2, { id: 7, value: VelocityDeviceType.SurfaceCh2, text: 'Surface Velocity' }],
]);

export const ModeList = <Array<MonitoringPointElement>>[
    { id: 1, value: 0, text: 'Normal' },
    { id: 2, value: 1, text: 'High Power' },
];

export const BlankingList = <Array<MonitoringPointElement>>[
    { id: 1, value: 0, text: 'Auto' },
    { id: 2, value: 2, text: '2' },
    { id: 3, value: 3, text: '3' },
    { id: 4, value: 4, text: '4' },
    { id: 5, value: 5, text: '5' },
    { id: 6, value: 6, text: '6' },
    { id: 7, value: 7, text: '7' },
    { id: 8, value: 8, text: '8' },
    { id: 9, value: 9, text: '9' },
    { id: 10, value: 10, text: '10' },
    { id: 11, value: 11, text: '11' },
    { id: 12, value: 12, text: '12' },
];

export const MetricBlankingList = <Array<MonitoringPointElement>>[
    { id: 1, value: 0, text: 'Auto' },
    { id: 2, value: 25.4, text: '25.4' },
    { id: 3, value: 50.8, text: '50.8' },
    { id: 4, value: 76.2, text: '76.2' },
    { id: 5, value: 101.6, text: '101.6' },
    { id: 6, value: 127, text: '127' },
    { id: 7, value: 152.4, text: '152.4' },
    { id: 8, value: 177.8, text: '177.8' },
    { id: 9, value: 203.2, text: '203.2' },
    { id: 10, value: 228.6, text: '228.6' },
    { id: 11, value: 254, text: '254' },
    { id: 12, value: 279.4, text: '279.4' },
    { id: 13, value: 304.8, text: '304.8' },
];

export const SVTransmitFrequencyList = [
    { id: 1, text: 'Low', value: 0 },
    { id: 2, text: 'Medium', value: 1 },
    { id: 3, text: 'High', value: 2 },
];

export const DataDeliveryFastMode = [
    { id: 1, text: 'OFF', value: false },
    { id: 3, text: '15 Minutes', value: true },
];

export const EchoInfoSensorOptions = [{ id: 1, text: 'ADD_EDIT.LONG_RANGE_DEPTH', value: 'ADD_EDIT.LONG_RANGE_DEPTH' }];

export const RainInfoSensorOptions = [{ id: 1, text: 'ADD_EDIT.RAIN', value: 'ADD_EDIT.RAIN' }];

export const DataLogModeOptions = [
    { id: 1, text: 'Synchronous', value: 0 },
    { id: 2, text: 'Asynchronous', value: 1 },
];

export const PVTransmitFrequencyList = [
    { id: 1, text: 'Normal', value: 0 },
    { id: 1, text: 'High', value: 1 },
];

export const VelocityAcquisition = <Array<MonitoringPointElement>>[
    { id: 1, value: 0, text: 'Auto' },
    { id: 4, value: 3, text: 'High' },
    { id: 2, value: 1, text: 'Low' },
    { id: 3, value: 2, text: 'Medium' },
];

export const MonitoringPoints = <Array<MonitoringPointElement>>[MP_ONE, MP_TWO];

export const ChannelList = <Array<ChannelElement>>[CHANNEL_ONE, CHANNEL_TWO];

export const SensorsList = [
    { id: 1, value: 0, text: 'Monitor' },
    { id: 2, value: 1, text: 'Channel 1' },
    { id: 3, value: 2, text: 'Channel 2' },
];

export const CommunicationTypes = <Array<MonitorAddLocationSelectable>>[
    { monitorSeriesID: 4, value: '4', text: 'TCP/IP' },
    { monitorSeriesID: 1, value: '1', text: 'Serial' },
    { monitorSeriesID: 6, value: '6', text: 'Bluetooth' },
];

export const SVDurationList = [
    { id: 1, text: '1 second', value: 1 },
    { id: 2, text: '2 seconds', value: 2 },
    { id: 3, text: '3 seconds', value: 3 },
    { id: 4, text: '4 seconds', value: 4 },
    { id: 5, text: '5 seconds', value: 5 },
];

export const DataDeliveryNormalRateList = [
    { id: 1, text: 'OFF', value: 0 },
    { id: 2, text: `5 Minutes`, value: 300 },
    { id: 3, text: `15 Minutes`, value: 900 },
    { id: 4, text: `1 Hour`, value: 3600 },
    { id: 5, text: `2 Hours`, value: 7200 },
    { id: 6, text: `3 Hours`, value: 10800 },
    { id: 7, text: `4 Hours`, value: 14400 },
    { id: 8, text: `6 Hours`, value: 21600 },
    { id: 9, text: `8 Hours`, value: 28800 },
    { id: 10, text: `12 Hours`, value: 43200 },
    { id: 11, text: `24 Hours`, value: 86400 },
];

export const FlexDataDeliveryFastRateList = [
    { id: 3, text: `15 Minutes`, value: 900 },
    { id: 4, text: `30 Minutes`, value: 1800 },
    { id: 5, text: `45 Minutes`, value: 2700 },
    { id: 6, text: `1 Hour`, value: 3600 },
    { id: 7, text: `1.5 Hours`, value: 5400 },
    { id: 8, text: `2 Hours`, value: 7200 },
    { id: 9, text: `3 Hours`, value: 10800 },
    { id: 10, text: `4 Hours`, value: 14400 },
    { id: 11, text: `6 Hours`, value: 21600 },
    { id: 12, text: `8 Hours`, value: 28800 },
    { id: 13, text: `12 Hours`, value: 43200 },
    { id: 14, text: `24 Hours`, value: 86400 },
];

export const FlexDataDeliveryNormalRateList = [
    { id: 3, text: `15 Minutes`, value: 900 },
    { id: 4, text: `30 Minutes`, value: 1800 },
    { id: 5, text: `45 Minutes`, value: 2700 },
    { id: 6, text: `1 Hour`, value: 3600 },
    { id: 7, text: `1.5 Hours`, value: 5400 },
    { id: 8, text: `2 Hours`, value: 7200 },
    { id: 9, text: `3 Hours`, value: 10800 },
    { id: 10, text: `4 Hours`, value: 14400 },
    { id: 11, text: `6 Hours`, value: 21600 },
    { id: 12, text: `8 Hours`, value: 28800 },
    { id: 13, text: `12 Hours`, value: 43200 },
    { id: 14, text: `24 Hours`, value: 86400 },
];

export const DataDeliveryFastRateList = [
    { id: 1, text: 'OFF', value: false },
    { id: 3, text: `15 Minutes`, value: true },
];

export const SampleRateList = <Array<SampleRateElement>>[
    { id: 1, value: 60, text: '1 minute' },
    { id: 2, value: 120, text: '2 minutes' },
    { id: 3, value: 300, text: '5 minutes' },
    { id: 4, value: 900, text: '15 minutes' },
    { id: 5, value: 1800, text: '30 minutes' },
    { id: 6, value: 3600, text: '1 hour' },
    { id: 7, value: 7200, text: '2 hours' },
    { id: 10, value: 43200, text: '12 hours' },
    { id: 11, value: 86400, text: '24 hours' },
];

export const ScanRateList = <Array<ScanRateElement>>[
    {id: 0, value: 0, text:'Normal'},
    {id: 1, value: 1, text:'Fast'},
];

export const FlexSampleRateList = <Array<SampleRateElement>>[
    { id: 1, value: 60, text: '1 minute' },
    { id: 2, value: 120, text: '2 minutes' },
    { id: 3, value: 300, text: '5 minutes' },
    { id: 4, value: 900, text: '15 minutes' },
    { id: 5, value: 1800, text: '30 minutes' },
    { id: 6, value: 3600, text: '1 hour' },
    { id: 7, value: 7200, text: '2 hours' },
    { id: 8, value: 14400, text: '4 hours' },
    { id: 9, value: 28800, text: '8 hours' },
    { id: 10, value: 43200, text: '12 hours' },
    { id: 11, value: 86400, text: '24 hours' },
];

export const ForeSiteSampleRateList = <Array<SampleRateElement>>[
    { id: 3, value: 300, text: '5 minutes' },
    { id: 4, value: 900, text: '15 minutes' },
    { id: 5, value: 1800, text: '30 minutes' },
    { id: 6, value: 3600, text: '1 hour' },
    { id: 7, value: 7200, text: '2 hours' },
    { id: 8, value: 14400, text: '4 hours' },
];

export const ForeSiteNormalDeliveryList = <Array<SampleRateElement>>[
    { id: 4, text: `1 Hour`, value: 1 },
    { id: 5, text: `2 Hours`, value: 2 },
    { id: 6, text: `3 Hours`, value: 3 },
    { id: 7, text: `4 Hours`, value: 4 },
    { id: 8, text: `6 Hours`, value: 6 },
    { id: 9, text: `8 Hours`, value: 8 },
    { id: 10, text: `12 Hours`, value: 12 },
    { id: 11, text: `24 Hours`, value: 24 },
];

export const LocationStatuses = [
    { key: LocationStatus.Active, name: 'Active' },
    { key: LocationStatus.Maintenance, name: 'Maintenance' },
    { key: LocationStatus.Inactive, name: 'Inactive' },
];

export const LocationStatusesWithNoMaintenance = [
    { key: LocationStatus.Active, name: 'Active' },
    { key: LocationStatus.Inactive, name: 'Inactive' },
];

export const FlowLossAlarmTypes = [
    { id: FlowLossType.Flow, name: FLOW },
    { id: FlowLossType.Depth, name: DEPTH },
];

export const FlowLossSensitivityList = [
    { id: FlowLossSensitivity.Low, name: LOW, isChecked: true },
    { id: FlowLossSensitivity.Medium, name: MEDIUM },
    { id: FlowLossSensitivity.High, name: HIGH },
];

export const FlowLossPercentageOptions = () => {
    const result: Selectable[] = [];

    for (let i = 20; i <= 80; i = i + 5) {
        result.push({
            id: i,
            name: String(i),
            isChecked: i === DEFAULT_FLOW_LOSS_PERCENTAGE,
        });
    }

    return result;
};

export const MaintenanceIntervalHours = (): number[] => {
    const result: number[] = [];

    for (let i = 1; i <= 48; i++) {
        result.push(i);
    }

    return result;
};

export const MaxVelocityMetricValues = (isParaFlowSurface = false) => {
    if (isParaFlowSurface) {
        return [
            { value: 0, text: 'Auto' },
            { value: 0.6096, text: '0.6096' },
            { value: 0.9144, text: '0.9144' },
            { value: 1.2192, text: '1.2192' },
            { value: 1.524, text: '1.524' },
            { value: 1.8288, text: '1.8288' },
            { value: 2.286, text: '2.286' },
            { value: 3.048, text: '3.048' },
            { value: 3.6576, text: '3.6576' },
            { value: 4.572, text: '4.572' },
            { value: 9.144, text: '9.144' },
        ];
    }

    return [
        { value: 0, text: 'Auto' },
        { value: 0.6096, text: '0.6096' },
        { value: 0.9144, text: '0.9144' },
        { value: 1.2192, text: '1.2192' },
        { value: 1.524, text: '1.524' },
        { value: 1.8288, text: '1.8288' },
        { value: 2.286, text: '2.286' },
        { value: 3.048, text: '3.048' },
        { value: 3.81, text: '3.81' },
        { value: 4.572, text: '4.572' },
        { value: 6.096, text: '6.096' },
        { value: 7.62, text: '7.62' },
        { value: 9.144, text: '9.144' },
    ];
};

export const MaxVelocityMetricValuesForParaFlow = [
    { value: 0, text: 'Auto' },
    { value: 0.6096, text: '0.6096' },
    { value: 0.9144, text: '0.9144' },
    { value: 1.2192, text: '1.2192' },
    { value: 1.524, text: '1.524' },
    { value: 1.8288, text: '1.8288' },
    { value: 2.286, text: '2.286' },
    { value: 3.048, text: '3.048' },
    { value: 3.6576, text: '3.6576' },
    { value: 4.572, text: '4.572' },
    { value: 9.144, text: '9.144' },
];

export const MaxVelocityNonMetricValuesForParaFlow = [
    { value: 0, text: 'Auto' },
    { value: 2, text: '2' },
    { value: 3, text: '3' },
    { value: 4, text: '4' },
    { value: 5, text: '5' },
    { value: 6, text: '6' },
    { value: 7.5, text: '7.5' },
    { value: 10, text: '10' },
    { value: 12, text: '12' },
    { value: 15, text: '15' },
    { value: 30, text: '30' },
];

export const MaxVelocityMetricValuesNonParaFlow = [
    { value: 0, text: 'Auto' },
    { value: 0.6096, text: '0.6096' },
    { value: 0.9144, text: '0.9144' },
    { value: 1.2192, text: '1.2192' },
    { value: 1.524, text: '1.524' },
    { value: 1.8288, text: '1.8288' },
    { value: 2.286, text: '2.286' },
    { value: 3.048, text: '3.048' },
    { value: 3.81, text: '3.81' },
    { value: 4.572, text: '4.572' },
    { value: 6.096, text: '6.096' },
    { value: 7.62, text: '7.62' },
    { value: 9.144, text: '9.144' },
];

export const MaxVelocityNonMetricValuesNonParaFlow = [
    { value: 0, text: 'Auto' },
    { value: 2, text: '2' },
    { value: 3, text: '3' },
    { value: 4, text: '4' },
    { value: 5, text: '5' },
    { value: 6, text: '6' },
    { value: 7.5, text: '7.5' },
    { value: 10, text: '10' },
    { value: 12.5, text: '12.5' },
    { value: 15, text: '15' },
    { value: 20, text: '20' },
    { value: 25, text: '25' },
    { value: 30, text: '30' },
];

export const MaxVelocityNonMetricValues = (isParaFlowSurface = false) => {
    if (isParaFlowSurface) {
        return [
            { value: 0, text: 'Auto' },
            { value: 2, text: '2' },
            { value: 3, text: '3' },
            { value: 4, text: '4' },
            { value: 5, text: '5' },
            { value: 6, text: '6' },
            { value: 7.5, text: '7.5' },
            { value: 10, text: '10' },
            { value: 12, text: '12' },
            { value: 15, text: '15' },
            { value: 30, text: '30' },
        ];
    }

    return [
        { value: 0, text: 'Auto' },
        { value: 2, text: '2' },
        { value: 3, text: '3' },
        { value: 4, text: '4' },
        { value: 5, text: '5' },
        { value: 6, text: '6' },
        { value: 7.5, text: '7.5' },
        { value: 10, text: '10' },
        { value: 12.5, text: '12.5' },
        { value: 15, text: '15' },
        { value: 20, text: '20' },
        { value: 25, text: '25' },
        { value: 30, text: '30' },
    ];
};

export const BatteryVoltage = [
    { value: 0, text: 'LOCATION_CARD.STORE_EVERY_READING' },
    { value: 1, text: 'LOCATION_CARD.STORE_AT_MIDNIGHT' },
];

export const CheckInHour12HrOptions = [
    { value: 0, text: '12 AM' },
    { value: 1, text: '1 AM' },
    { value: 2, text: '2 AM' },
    { value: 3, text: '3 AM' },
    { value: 4, text: '4 AM' },
    { value: 5, text: '5 AM' },
    { value: 6, text: '6 AM' },
    { value: 7, text: '7 AM' },
    { value: 8, text: '8 AM' },
    { value: 9, text: '9 AM' },
    { value: 10, text: '10 AM' },
    { value: 11, text: '11 AM' },
    { value: 12, text: '12 PM' },
    { value: 13, text: '1 PM' },
    { value: 14, text: '2 PM' },
    { value: 15, text: '3 PM' },
    { value: 16, text: '4 PM' },
    { value: 17, text: '5 PM' },
    { value: 18, text: '6 PM' },
    { value: 19, text: '7 PM' },
    { value: 20, text: '8 PM' },
    { value: 21, text: '9 PM' },
    { value: 22, text: '10 PM' },
    { value: 23, text: '11 PM' },
];

export const CheckInHour24HrOptions = [
    { value: 0, text: '0:00' },
    { value: 1, text: '1:00' },
    { value: 2, text: '2:00' },
    { value: 3, text: '3:00' },
    { value: 4, text: '4:00' },
    { value: 5, text: '5:00' },
    { value: 6, text: '6:00' },
    { value: 7, text: '7:00' },
    { value: 8, text: '8:00' },
    { value: 9, text: '9:00' },
    { value: 10, text: '10:00' },
    { value: 11, text: '11:00' },
    { value: 12, text: '12:00' },
    { value: 13, text: '13:00' },
    { value: 14, text: '14:00' },
    { value: 15, text: '15:00' },
    { value: 16, text: '16:00' },
    { value: 17, text: '17:00' },
    { value: 18, text: '18:00' },
    { value: 19, text: '19:00' },
    { value: 20, text: '20:00' },
    { value: 21, text: '21:00' },
    { value: 22, text: '22:00' },
    { value: 23, text: '23:00' },
];

export enum CommportValue {
    None = 'None',
    Inclinometer = 'Inclinometer',
    Sampler = 'Sampler',
}

export const CommportOptions: ComportOption[] = [
    { value: CommportValue.None, text: 'COMMON.NONE' },
    { value: CommportValue.Inclinometer, text: 'COMMON.INCLINOMETER' },
    { value: CommportValue.Sampler, text: 'COMMON.SAMPLER' },
];

export const SamplerTypes: SamplerTypeOption[] = [
    { id: 0, name: 'COMMON.OFF_TEXT' },
    { id: 1, name: 'COMMON.DEPTH' },
    { id: 2, name: 'COMMON.FLOW_RATE' },
    { id: 3, name: 'COMMON.FLOW_TOTAL' },
];

export const SamplerPerWakeupOptions = () => {
    const result: Selectable[] = [];

    for (let i = 1; i <= 10; i++) {
        result.push({ id: i, name: String(i) });
    }

    return result;
};

export const SamplerPulseLengthOptions = () => {
    const result: Selectable[] = [];

    for (let i = 1; i <= 10; i++) {
        result.push({ id: i, name: String(i) });
    }

    return result;
};

export const FlowParameterOptions = [
    { id: QCONTINUITY_ENTITY, name: QCONTINUITY },
    { id: QLOOKUP_ENTITY, name: QLOOKUP },
    { id: QFLUME_ENTITY, name: QFLUME },
    { id: QWEIR_ENTITY, name: QWEIR },
];

// Series Ids for Rain gauge and USGS locations
export const RainUSGSLocationTypes = [MonitorIdentifier.None, MonitorIdentifier.Unknown, MonitorIdentifier.RainAlertIII];

export const FlexFlowTypeOptions: FlowTypeOption[] = [
    { text: 'ADD_EDIT.FLOW_TYPE_NORMAL', value: FlowType.Normal },
    { text: 'ADD_EDIT.FLOW_TYPE_WET_STAGNANT', value: FlowType.WetStagnant },
    { text: 'ADD_EDIT.FLOW_TYPE_USUALLY_DRY', value: FlowType.UsuallyDry },
    { text: 'ADD_EDIT.FLOW_TYPE_PUMP_STATION', value: FlowType.PumpStation },
];
